/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ComponentProps } from 'react';
import type { ShopperSearch } from '@/scapi';
import { cn } from '@/lib/utils';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { BadgeDetail } from '@/types/config';
import {
    productBadgesVariants,
    productBadgeSemanticVariants,
    productBadgeColorClasses,
    type ProductBadgesVariantsProps,
} from './product-badge-variants';
import { Badge } from '@/components/ui/badge';
import { getProductBadges } from '@/lib/product/product-badges';

/**
 * Product Badge Component - Wrapper around core Badge with semantic styling
 *
 * This component reuses the core Badge component and applies semantic color styling
 * through custom className. It maintains the same API as the core Badge but adds
 * product-specific semantic variants (success, warning, info).
 *
 * @example
 * <ProductBadge variant="success">New</ProductBadge>
 * <ProductBadge variant="warning">Sale</ProductBadge>
 * <ProductBadge variant="info">Exclusive</ProductBadge>
 */
interface ProductBadgeProps extends Omit<ComponentProps<typeof Badge>, 'variant'> {
    variant?: 'success' | 'warning' | 'info' | 'default' | 'secondary' | 'destructive' | 'outline';
}

function ProductBadge({ variant = 'default', className, ...props }: ProductBadgeProps) {
    // Map semantic variants to their CSS classes
    const semanticClasses = {
        success: productBadgeSemanticVariants.success,
        warning: productBadgeSemanticVariants.warning,
        info: productBadgeSemanticVariants.info,
    };

    // Use semantic styling for product variants, fall back to core Badge for others
    const isSemanticVariant = variant === 'success' || variant === 'warning' || variant === 'info';
    const badgeVariant = isSemanticVariant ? 'default' : variant;
    const semanticClass = isSemanticVariant ? semanticClasses[variant] : '';

    return <Badge variant={badgeVariant} className={cn(semanticClass, className)} {...props} />;
}

/**
 * Product Badges Container Component
 *
 * Renders a collection of product badges based on product properties and configuration.
 * This component automatically determines which badges to show based on:
 * - Product properties (c_badge enum values such as new/sale, boolean flags such as c_isLimited)
 * - Custom properties
 * - Promotions
 * - Stock status
 *
 * Badge colors come from each badge's configured `color`, mapped to theme classes by
 * `productBadgeColorClasses` in ./product-badge-variants.ts.
 *
 * @example
 * <ProductBadges product={product} maxBadges={3} />
 */
interface ProductBadgesProps extends ComponentProps<'div'>, ProductBadgesVariantsProps {
    product: ShopperSearch.schemas['ProductSearchHit'];
    badgeDetails?: BadgeDetail[];
    maxBadges?: number;
    'aria-label'?: string;
}

const ProductBadges = ({
    className,
    product,
    badgeDetails,
    maxBadges = 3,
    variant,
    size,
    'aria-label': ariaLabel,
    ...props
}: ProductBadgesProps) => {
    const config = useConfig();
    // Use runtime config if badgeDetails not provided
    const finalBadgeDetails = badgeDetails ?? config.global.badges;

    const { badges, hasBadges } = getProductBadges({
        product,
        badgeDetails: finalBadgeDetails,
        maxBadges,
    });

    if (!hasBadges) {
        return null;
    }

    const defaultAriaLabel = `Product badges: ${badges.map((b) => b.label).join(', ')}`;

    return (
        <div
            className={cn(productBadgesVariants({ variant, size }), className)}
            aria-label={ariaLabel || defaultAriaLabel}
            role="group"
            {...props}>
            {badges.map((badge) => (
                <ProductBadge
                    key={`${badge.propertyName}:${badge.value ?? ''}`}
                    className={productBadgeColorClasses[badge.color]}
                    role="status"
                    aria-label={`${badge.label} product`}>
                    {badge.label}
                </ProductBadge>
            ))}
        </div>
    );
};

export { ProductBadges, ProductBadge };
